import { cn } from '@/lib/utils'
import type { ReceiptElement } from '../utils/receiptTypes'

interface ReceiptPreviewProps {
  elements: ReceiptElement[]
  className?: string
}

/**
 * Renders a ReceiptElement[] array as styled JSX to look like a thermal receipt.
 * Used for print preview — works on all platforms (pure React, no native code).
 */
export function ReceiptPreview({ elements, className }: ReceiptPreviewProps) {
  return (
    <div
      className={cn(
        'bg-white text-black font-mono text-xs leading-tight',
        'w-[300px] mx-auto px-4 py-6 shadow-lg rounded',
        className,
      )}
    >
      {elements.map((el, i) => (
        <ReceiptElementView key={i} element={el} />
      ))}
    </div>
  )
}

function ReceiptElementView({ element }: { element: ReceiptElement }) {
  switch (element.type) {
    case 'text':
      return (
        <p
          className={cn(
            'whitespace-pre-wrap break-words my-0',
            element.align === 'center' && 'text-center',
            element.align === 'right' && 'text-right',
            element.bold && 'font-bold',
          )}
        >
          {element.text}
        </p>
      )

    case 'columns':
      return (
        <div className="flex justify-between gap-1 my-0">
          <span className={cn('whitespace-pre-wrap break-words min-w-0', element.leftBold && 'font-bold')}>
            {element.left}
          </span>
          <span className={cn('shrink-0 text-right', element.rightBold && 'font-bold')}>
            {element.right}
          </span>
        </div>
      )

    case 'line': {
      const char = element.char ?? '_'
      return (
        <div className="text-center overflow-hidden my-0.5" aria-hidden>
          {char.repeat(40)}
        </div>
      )
    }

    case 'image':
      // Thermal printers print the logo in black and white.
      return element.src ? (
        <img
          src={element.src}
          alt=""
          className="mx-auto my-1 max-h-24 max-w-full object-contain grayscale"
          onError={(event) => { event.currentTarget.hidden = true }}
        />
      ) : (
        <div className="text-center py-2 text-gray-400 text-[10px]">
          [Logo]
        </div>
      )

    case 'chunkBreak':
      return null

    case 'qrcode':
      // Show QR code as a placeholder box with URL
      return (
        <div className="text-center py-2">
          <div className="inline-block border border-gray-300 p-2 text-[10px] text-gray-500">
            QR: Maps
          </div>
        </div>
      )

    case 'feed':
      return (
        <>
          {Array.from({ length: element.lines ?? 1 }, (_, i) => (
            <div key={i} className="h-3" />
          ))}
        </>
      )
  }
}
